'use client';

import * as React from 'react';
import { styled } from '@linaria/react';

import Wire from './Wire';
import Padlock from './Padlock';

interface Props {
  label: string;
  choices: Array<string>;
  choiceIndex?: number;
}

const MOBILE_CUTOFF = 550;

function LockedVariable({ label, choices, choiceIndex = 0 }: Props) {
  const id = React.useId();

  const choiceRefs = React.useRef<{
    [index: number]: HTMLElement;
  }>({});

  return (
    <>
      <Wrapper>
        <Label>
          <Prefix>const</Prefix>
          {label}
        </Label>
        <WireWrapper>
          <Wire choiceRefs={choiceRefs} choiceIndex={choiceIndex} />
          <FloatingPadlock />
        </WireWrapper>
        <ChoicesWrapper>
          {choices.map((choice, index) => {
            const isSelected = index === choiceIndex;
            return (
              <Choice
                ref={(elem) => {
                  if (elem) {
                    choiceRefs.current[index] = elem;
                  }
                }}
                htmlFor={`choice-${id}-${index}`}
                key={index}
              >
                <Background data-is-selected={isSelected}>
                  {isSelected && (
                    <Hook
                      viewBox="0 0 14 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9.00225 2C4.03169 2 0 6.02944 0 11C0 15.9706 4.03169 20 9.00225 20H11.5C13.7503 20 14 22 14 22V0.5C14 0.5 13 2 11.5 2H9.00225ZM9 15C11.2091 15 13 13.2091 13 11C13 8.79086 11.2091 7 9 7C6.79086 7 5 8.79086 5 11C5 13.2091 6.79086 15 9 15Z"
                        fill="hsl(50deg 100% 88%)"
                      />
                    </Hook>
                  )}
                </Background>
                <ChoiceLabel>{choice}</ChoiceLabel>
                <Radio
                  disabled={true}
                  type="radio"
                  name={`reassignable-variable-${id}`}
                  id={`choice-${id}-${index}`}
                  checked={isSelected}
                />
                {isSelected && (
                  <FrontHook
                    viewBox="0 0 14 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M9.00225 2C4.03169 2 0 6.02944 0 11C0 15.9706 4.03169 20 9.00225 20H11.5C13.7503 20 14 22 14 22V0.5C14 0.5 13 2 11.5 2H9.00225ZM9 15C11.2091 15 13 13.2091 13 11C13 8.79086 11.2091 7 9 7C6.79086 7 5 8.79086 5 11C5 13.2091 6.79086 15 9 15Z"
                      fill="hsl(50deg 100% 88%)"
                    />
                  </FrontHook>
                )}
              </Choice>
            );
          })}
        </ChoicesWrapper>
      </Wrapper>
      <MobileAlternative>
        <img
          alt="Diagram showing that the “fruits” constant is locked to a specific value."
          src="/images/the-const-deception/locked-variable-thumbnail.png"
          style={{
            aspectRatio: '1200 / 530',
            marginLeft: -16,
            marginRight: -16,
            maxWidth: 'calc(100% + 32px)',
          }}
        />
      </MobileAlternative>
    </>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  isolation: isolate;

  @media (max-width: ${MOBILE_CUTOFF}px) {
    display: none;
  }
`;

const FloatingPadlock = styled(Padlock)`
  position: absolute;
  left: 0;
  right: 0;
  margin: auto;
  top: 0%;
`;

const Label = styled.div`
  position: relative;
  font-size: 1.25rem;
  font-family: var(--font-family-mono);
`;

const Prefix = styled.span`
  position: absolute;
  top: 5px;
  left: 0;
  transform: translateY(-100%);
  font-size: 0.75rem;
  color: var(--color-gray-700);
`;

const WireWrapper = styled.div`
  position: relative;
  z-index: 2;
  flex: 1;
  align-self: stretch;
`;

const ChoicesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Choice = styled.label`
  position: relative;
  color: black;
  border-radius: 4px;
  padding: 14px 18px;
  font-family: var(--font-family-mono);
  white-space: pre-wrap;
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: white;
  /* prettier-ignore */
  filter:
    drop-shadow(0px 1px 2px hsl(0deg 0% 0% / 0.1))
    drop-shadow(0px 2px 4px hsl(0deg 0% 0% / 0.1))
    drop-shadow(0px 4px 8px hsl(0deg 0% 0% / 0.1))
    drop-shadow(0px 8px 16px hsl(0deg 0% 0% / 0.1));

  &[data-is-selected='false'] {
    opacity: 0.5;
  }
  html[data-color-mode='dark'] &[data-is-selected='false'] {
    opacity: 0.75;
  }

  &[data-is-selected='true'] {
    background: hsl(50deg 100% 88%);
  }
`;

const ChoiceLabel = styled.div`
  position: relative;
`;

const Radio = styled.input`
  position: absolute;
  inset: 0;
  appearance: none;
  cursor: pointer;
  border-radius: inherit;

  &:disabled {
    cursor: not-allowed;
  }
`;

const Hook = styled.svg`
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 0;
  margin: auto;
  left: -21px;
  width: 21px;
  height: 33px;
`;

const FrontHook = styled(Hook)`
  z-index: 3;
  clip-path: polygon(0 50%, 100% 50%, 100% 100%, 0% 100%);
`;

const MobileAlternative = styled.div`
  display: none;

  @media (max-width: ${MOBILE_CUTOFF}px) {
    display: block;
  }
`;

export default LockedVariable;
